/**
 * B3 trace ids are 16 or 32 hex characters. The placement pipeline stores
 * `x-b3-traceid`; the edge exposes that value to the browser as `x-trace-id`
 * and copies it to `atl-traceid`. `atl-request-id` is a different, dashed id.
 */
const B3_TRACE_ID = /^(?:[0-9a-f]{32}|[0-9a-f]{16})$/i;

const isB3TraceId = (value: unknown): value is string =>
	typeof value === 'string' && B3_TRACE_ID.test(value);

/**
 * Read the placement response trace id.
 *
 * Prefer `x-trace-id`, then `atl-traceid`, matching `getTraceId` in
 * linking-platform. Returns undefined when the header is missing or not a B3 id,
 * or when the headers can't be read: hosts and tests inject the fetcher, so the
 * response may be a fake without real `headers`.
 */
export const readPlacementTraceId = (response: Response): string | undefined => {
	try {
		const value = response.headers.get('x-trace-id') ?? response.headers.get('atl-traceid');
		return isB3TraceId(value) ? value : undefined;
	} catch {
		return undefined;
	}
};

/**
 * Trace id that `useFetchPlacementData` added to a placement response body.
 * Undefined for bodies that did not come from that hook, or whose response had no trace header.
 */
export const getPlacementTraceId = (data: unknown): string | undefined => {
	if (data === null || typeof data !== 'object' || !('traceId' in data)) {
		return undefined;
	}
	const { traceId } = data as { traceId?: unknown };
	return isB3TraceId(traceId) ? traceId : undefined;
};
